import { TransformationVariant } from '@amzn/fire-tv-campaign-automation-service';
import { createContext, useContext, useState, useCallback, FC, ReactNode, useMemo } from 'react';

import { AssetStatus } from 'src/types';

export type AppMode = 'standard' | 'smart';

export type ImageSource = 'upload' | 'catalog' | null;

export type LogoSource = 'upload' | 'construct' | null;

export enum WizardStep {
  DEVICE_FAMILY = 'device_family',
  PLACEMENT = 'placement',
  MARKETPLACE = 'marketplace',
  LOCALE = 'locale',
  IMAGE_SOURCE = 'image_source',
  CATALOG = 'catalog',
  UPLOAD = 'upload',
  ASSET_UPLOAD = 'asset_upload',
  RESULTS = 'results',
  LOGO = 'logo',
  VIDEO = 'video',
}

export interface BatchTransformResult {
  variant: TransformationVariant;
  transformationId?: string;
  status: AssetStatus;
  transformedAssetKey?: string;
  previewUrl?: string;
  error?: string;
}

interface SmartUploadContextType {
  mode: AppMode;
  setMode: (mode: AppMode) => void;
  wizardStep: WizardStep;
  setWizardStep: (step: WizardStep) => void;
  imageSource: ImageSource;
  setImageSource: (source: ImageSource) => void;
  batchResults: BatchTransformResult[];
  setBatchResults: (results: BatchTransformResult[]) => void;
  updateBatchResult: (
    variant: TransformationVariant,
    update: Partial<BatchTransformResult>,
  ) => void;
  selectedResultIndex: number | null;
  setSelectedResultIndex: (index: number | null) => void;
  logoTransformResult: BatchTransformResult | null;
  setLogoTransformResult: (result: BatchTransformResult | null) => void;
  logoSource: LogoSource;
  setLogoSource: (source: LogoSource) => void;
  videoTransformResult: BatchTransformResult | null;
  setVideoTransformResult: (result: BatchTransformResult | null) => void;
  questionsCollapsed: boolean;
  setQuestionsCollapsed: (collapsed: boolean) => void;
  wizardCollapsed: boolean;
  setWizardCollapsed: (collapsed: boolean) => void;
  resetSmartUpload: () => void;
}

export const SmartUploadContext = createContext<SmartUploadContextType | undefined>(undefined);

export const SmartUploadProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [mode, setMode] = useState<AppMode>('standard');
  const [wizardStep, setWizardStep] = useState<WizardStep>(WizardStep.DEVICE_FAMILY);
  const [imageSource, setImageSource] = useState<ImageSource>(null);
  const [batchResults, setBatchResults] = useState<BatchTransformResult[]>([]);
  const [selectedResultIndex, setSelectedResultIndex] = useState<number | null>(null);
  const [logoTransformResult, setLogoTransformResult] = useState<BatchTransformResult | null>(null);
  const [logoSource, setLogoSource] = useState<LogoSource>(null);
  const [videoTransformResult, setVideoTransformResult] = useState<BatchTransformResult | null>(
    null,
  );
  const [questionsCollapsed, setQuestionsCollapsed] = useState(false);
  const [wizardCollapsed, setWizardCollapsed] = useState(false);

  const updateBatchResult = useCallback(
    (variant: TransformationVariant, update: Partial<BatchTransformResult>) => {
      setBatchResults((prev) => {
        const idx = prev.findIndex((r) => r.variant === variant);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = { ...next[idx], ...update };
          return next;
        }
        return [...prev, { variant, status: AssetStatus.TRANSFORMATION_IN_PROGRESS, ...update }];
      });
    },
    [],
  );

  const resetSmartUpload = useCallback(() => {
    setWizardStep(WizardStep.DEVICE_FAMILY);
    setImageSource(null);
    setBatchResults([]);
    setSelectedResultIndex(null);
    setLogoTransformResult(null);
    setLogoSource(null);
    setVideoTransformResult(null);
    setQuestionsCollapsed(false);
    setWizardCollapsed(false);
  }, []);

  const value = useMemo(
    () => ({
      mode,
      setMode,
      wizardStep,
      setWizardStep,
      imageSource,
      setImageSource,
      batchResults,
      setBatchResults,
      updateBatchResult,
      selectedResultIndex,
      setSelectedResultIndex,
      logoTransformResult,
      setLogoTransformResult,
      logoSource,
      setLogoSource,
      videoTransformResult,
      setVideoTransformResult,
      questionsCollapsed,
      setQuestionsCollapsed,
      wizardCollapsed,
      setWizardCollapsed,
      resetSmartUpload,
    }),
    [
      mode,
      wizardStep,
      imageSource,
      batchResults,
      selectedResultIndex,
      logoTransformResult,
      logoSource,
      videoTransformResult,
      questionsCollapsed,
      wizardCollapsed,
      updateBatchResult,
      resetSmartUpload,
    ],
  );

  return <SmartUploadContext.Provider value={value}>{children}</SmartUploadContext.Provider>;
};

export const useSmartUpload = (): SmartUploadContextType => {
  const context = useContext(SmartUploadContext);
  if (!context) throw new Error('useSmartUpload must be used within SmartUploadProvider');
  return context;
};
